import {
  SUBSCRIPTION_TIERS,
  useCustomQuery,
  WorkspaceFilterColumns,
  WorkspaceFilterModel,
  WorkspaceType,
} from '@gripp/shared-logic';
import {
  Colors,
  colors as CropCircleColors,
  filterScreenStyles,
  FormContext,
  Layout,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { keepPreviousData } from '@tanstack/react-query';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { array, string, object as yupObject } from 'yup';

export type WorkspacesFilterScreenHandle = {
  onReset: () => Promise<void>;
};

type WorkspacesFilterScreenProps = {
  filter: WorkspaceFilterModel | undefined;
  setFilter: (filter: any) => Promise<any>;
  validateListOrder: (filter: any) => Promise<any>;
};

const formSchema = yupObject({
  columns: array().of(string().optional().nullable()).optional().nullable(),
  subscriptionTiers: array()
    .of(string().optional().nullable())
    .optional()
    .nullable(),
  types: array().of(string().optional().nullable()).optional().nullable(),
});

export const WorkspacesFilterScreen = forwardRef<
  WorkspacesFilterScreenHandle,
  WorkspacesFilterScreenProps
>(function WorkspacesFilterScreen(
  { filter, setFilter, validateListOrder },
  ref
) {
  const { t } = useTranslation();
  const slideLayout = useSlideLayoutSafe();
  const filterTitle = t('common.filter');

  const serverFilter = filter
    ? new WorkspaceFilterModel(filter).getGraphQLFilter()
    : {};
  const { data: countData } = useCustomQuery({
    cacheKey: ['workspaces', 'filterCount', JSON.stringify(serverFilter)],
    query: `
      query ListWorkspacesCount($filter: ListWorkspacesFilter, $page: PageRequest) {
        workspace {
          listWorkspaces(filter: $filter, page: $page) {
            count
          }
        }
      }
    `,
    variables: { filter: serverFilter, page: { limit: 1, offset: 0 } },
    options: { enabled: true, placeholderData: keepPreviousData },
  });
  const resultsCount = countData?.workspace?.listWorkspaces?.count ?? 0;

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      columns: (() => {
        if (!filter?.columns) return ['name'];
        const cols = Array.from(
          new Set(
            (filter.columns as any[]).filter(
              (c: any): c is string => c != null && typeof c === 'string'
            )
          )
        );
        return cols.includes('name') ? cols : ['name', ...cols];
      })(),
      subscriptionTiers: filter?.subscriptionTiers || [],
      types: filter?.types || [],
    },
  });

  const { handleSubmit, watch, reset } = formMethods;
  const onSubmitRef = useRef(onSubmit);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const subscription = watch(() => {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
          handleSubmit(onSubmitRef.current)();
        }, 1000);
      });
      return () => {
        subscription.unsubscribe();
        clearTimeout(debounceTimerRef.current);
      };
    }
    return;
  }, [watch, handleSubmit]);

  useEffect(() => {
    slideLayout?.setColumnTitle(`${filterTitle} (${resultsCount})`);
    return () => slideLayout?.setColumnTitle(undefined);
  }, [slideLayout, filterTitle, resultsCount]);

  const resetForm = useCallback(() => {
    const defaultFilter = new WorkspaceFilterModel();
    let defaultColumns = defaultFilter.columns || ['name'];
    if (!defaultColumns.includes('name'))
      defaultColumns = ['name', ...defaultColumns];
    reset({ columns: defaultColumns, subscriptionTiers: [], types: [] });
  }, [reset]);

  useEffect(() => {
    if (!filter) resetForm();
  }, [filter, resetForm]);

  async function onSubmit(data: any) {
    if (data.columns) {
      let columns = Array.from(
        new Set(
          (data.columns as any[]).filter(
            (c: any): c is string => c != null && typeof c === 'string'
          )
        )
      );
      if (!columns.includes('name')) columns = ['name', ...columns];
      data.columns = columns;
    }
    const filterModel = new WorkspaceFilterModel({ ...filter, ...data });
    await Promise.all([setFilter(filterModel), validateListOrder(filterModel)]);
  }
  onSubmitRef.current = onSubmit;

  const onReset = useCallback(async () => {
    resetForm();
    const defaultFilter = new WorkspaceFilterModel();
    await Promise.all([
      setFilter(defaultFilter),
      validateListOrder(defaultFilter),
    ]);
  }, [resetForm, setFilter, validateListOrder]);

  useImperativeHandle(ref, () => ({ onReset }), [onReset]);

  return (
    <FormContext.Provider value={formMethods as any}>
      <View style={styles.container}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.scrollContent,
            filterScreenStyles.scrollContentPage,
          ]}
        >
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {t('workspace.filter.columnSelection')}
            </Text>
            <Controller
              control={formMethods.control}
              name="columns"
              render={({ field: { onChange, value } }) => {
                const currentValue = (value || []).filter(
                  (c): c is string => c != null
                );
                const selectableColumns = WorkspaceFilterColumns.filter(
                  (col) => col !== 'name'
                );
                const onPressColumn = (column: string) => {
                  if (column === 'name') return;
                  let newValue: string[];
                  if (currentValue.includes(column))
                    newValue = currentValue.filter((c) => c !== column);
                  else newValue = [...currentValue, column];
                  if (!newValue.includes('name'))
                    newValue = ['name', ...newValue];
                  onChange(newValue);
                };
                const effectiveValue = currentValue.includes('name')
                  ? currentValue
                  : ['name', ...currentValue];
                return (
                  <View style={styles.optionsContainer}>
                    <Pressable
                      key="name"
                      disabled={true}
                      style={[
                        styles.optionChip,
                        styles.optionChipSelected,
                        styles.optionChipDisabled,
                      ]}
                    >
                      <Text
                        style={[
                          styles.optionChipText,
                          styles.optionChipTextSelected,
                        ]}
                      >
                        {t('workspace.filter.columns.name')}
                      </Text>
                    </Pressable>
                    {selectableColumns.map((column) => {
                      const isSelected = effectiveValue.includes(column);
                      return (
                        <Pressable
                          key={column}
                          onPress={() => onPressColumn(column)}
                          style={[
                            styles.optionChip,
                            isSelected && styles.optionChipSelected,
                          ]}
                        >
                          <Text
                            style={[
                              styles.optionChipText,
                              isSelected && styles.optionChipTextSelected,
                            ]}
                          >
                            {t(`workspace.filter.columns.${column}`)}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                );
              }}
            />
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {t('workspace.filter.tierSelection')}
            </Text>
            <Controller
              control={formMethods.control}
              name="subscriptionTiers"
              render={({ field: { value, onChange } }) => {
                const toggleTier = (tierId: string) => {
                  const currentTiers = value || [];
                  const newTiers = currentTiers.includes(tierId)
                    ? currentTiers.filter((id) => id !== tierId)
                    : [...currentTiers, tierId];
                  onChange(newTiers);
                };
                return (
                  <View style={styles.optionsContainer}>
                    {SUBSCRIPTION_TIERS.map((tier) => {
                      const isSelected = (value || []).includes(tier.id);
                      return (
                        <Pressable
                          key={tier.id}
                          onPress={() => toggleTier(tier.id)}
                          style={[
                            styles.optionChip,
                            isSelected && styles.optionChipSelected,
                          ]}
                        >
                          <Text
                            style={[
                              styles.optionChipText,
                              isSelected && styles.optionChipTextSelected,
                            ]}
                          >
                            {tier.name}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                );
              }}
            />
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {t('workspace.filter.typeSelection')}
            </Text>
            <Controller
              control={formMethods.control}
              name="types"
              render={({ field: { value, onChange } }) => {
                const toggleType = (type: string) => {
                  const currentTypes = value || [];
                  const newTypes = currentTypes.includes(type)
                    ? currentTypes.filter((t) => t !== type)
                    : [...currentTypes, type];
                  onChange(newTypes);
                };
                return (
                  <View style={styles.optionsContainer}>
                    {Object.values(WorkspaceType).map((type) => {
                      const isSelected = (value || []).includes(type);
                      return (
                        <Pressable
                          key={type}
                          onPress={() => toggleType(type)}
                          style={[
                            styles.optionChip,
                            isSelected && styles.optionChipSelected,
                          ]}
                        >
                          <Text
                            style={[
                              styles.optionChipText,
                              isSelected && styles.optionChipTextSelected,
                            ]}
                          >
                            {t(`workspace.types.${type}`)}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                );
              }}
            />
          </View>
        </ScrollView>
      </View>
    </FormContext.Provider>
  );
});

const styles = StyleSheet.create({
  container: {
    ...Layout.container,
    backgroundColor: Colors.white,
    flex: 1,
    position: 'relative',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 100,
  },
  section: {
    paddingTop: 24,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: CropCircleColors.neutral[600],
    marginBottom: 12,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  optionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  optionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    backgroundColor: CropCircleColors.neutral[200],
    gap: 6,
  },
  optionChipSelected: {
    backgroundColor: Colors.black,
    borderColor: Colors.black,
  },
  optionChipDisabled: {
    opacity: 0.6,
  },
  optionChipText: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.black,
  },
  optionChipTextSelected: {
    color: Colors.white,
  },
});
